import React from 'react';
import { BrowserRouter, useLocation, useNavigate } from 'react-router-dom';

import { createAppActions } from './app/appActions';
import AppLayout from './app/AppLayout';
import Login from './app/Login';
import PasswordRecoveryScreen from './app/PasswordRecoveryScreen';
import { deriveViewState } from './app/viewState';
import { registerHandlers } from './handlers';
import { useAppEffects } from './hooks/useAppEffects';
import { useSidebarRefs } from './hooks/useSidebarRefs';
import { useStateUpdater } from './hooks/useStateUpdater';
import { useTimelineScroll } from './hooks/useTimelineScroll';
import { AppContext } from './store/AppContext';
import { useAppStore } from './store/appStore';
import { guessAoDocumentType, sanitizeStorageFilename } from './utils/tenderDocuments';

export default function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}

function AppInner() {
  const location = useLocation();
  const navigate = useNavigate();
  const store = useAppStore();
  const update = useStateUpdater();
  const appActions = createAppActions({ ...store, navigate });
  const timelineRefs = useTimelineScroll(store);
  const sidebarRefs = useSidebarRefs(store);

  // Traitements métier, sortis par domaine dans src/handlers/. `api` est rempli en une fois :
  // un domaine peut appeler les traitements d'un autre sans dépendre de l'ordre de création.
  const api = {};
  registerHandlers(api, {
    ...store,
    ...appActions,
    guessAoDocumentType,
    sanitizeStorageFilename,
    update,
  });
  useAppEffects({ store, api, loadProfile: appActions.loadProfile, location, navigate, update });

  if (store.authLoading)
    return (
      <div className="flex items-center justify-center h-[100vh] bg-[#f5f4f2] text-[13px] text-grey-light">
        Chargement…
      </div>
    );
  if (!store.session) return <Login handleMicrosoftLogin={appActions.handleMicrosoftLogin} />;
  if (store.isRecovery) return <PasswordRecoveryScreen />;

  const appContext = {
    ...api,
    ...appActions,
    ...deriveViewState(store),
    ...timelineRefs,
    ...sidebarRefs,
    navigate,
    sidebarCollapsed: store.sidebarCollapsed,
    update,
  };
  return (
    <AppContext.Provider value={appContext}>
      <AppLayout />
    </AppContext.Provider>
  );
}
